/**
 * Zoom da galeria da PDP em lightbox próprio (substitui o Magic Zoom Plus do tema anterior, que estava
 * com `zoomMode: off` e só servia para abrir a galeria ampliada).
 * Abre a imagem grande em tela cheia com navegação, fecha por Esc/clique fora e aproxima ao clicar
 * (ou com a roda do mouse), arrastando para deslocar. Respeita prefers-reduced-motion.
 */
(() => {
  const MAX_SCALE = 3;

  class Lightbox {
    constructor() {
      this.root = null;
      this.items = [];
      this.index = 0;
      this.scale = 1;
      this.offset = { x: 0, y: 0 };
    }

    build() {
      if (this.root) return;
      const strings = (window.theme && window.theme.strings) || {};
      this.root = document.createElement('div');
      this.root.className = 'mb-zoom';
      this.root.setAttribute('role', 'dialog');
      this.root.setAttribute('aria-modal', 'true');
      this.root.innerHTML = `
        <button class="mb-zoom-close" type="button" aria-label="${strings.close || 'X'}"></button>
        <button class="mb-zoom-prev" type="button" aria-label="${strings.previousSlide || ''}"></button>
        <button class="mb-zoom-next" type="button" aria-label="${strings.nextSlide || ''}"></button>
        <div class="mb-zoom-stage"><img alt=""></div>
      `;
      this.image = this.root.querySelector('img');
      this.stage = this.root.querySelector('.mb-zoom-stage');

      this.root.querySelector('.mb-zoom-close').addEventListener('click', () => this.close());
      this.root.querySelector('.mb-zoom-prev').addEventListener('click', () => this.go(-1));
      this.root.querySelector('.mb-zoom-next').addEventListener('click', () => this.go(1));
      this.root.addEventListener('click', (event) => {
        if (event.target === this.root || event.target === this.stage) this.close();
      });
      this.image.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setScale(this.scale > 1 ? 1 : 2);
      });
      this.stage.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          this.setScale(this.scale - Math.sign(event.deltaY) * 0.25);
        },
        { passive: false }
      );
      this.bindDrag();
      document.addEventListener('keydown', (event) => {
        if (!this.root.classList.contains('-open')) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft') this.go(-1);
        if (event.key === 'ArrowRight') this.go(1);
      });
      document.body.appendChild(this.root);
    }

    bindDrag() {
      let dragging = false;
      let start = { x: 0, y: 0 };
      const down = (event) => {
        if (this.scale <= 1) return;
        dragging = true;
        const point = event.touches ? event.touches[0] : event;
        start = { x: point.clientX - this.offset.x, y: point.clientY - this.offset.y };
      };
      const move = (event) => {
        if (!dragging) return;
        const point = event.touches ? event.touches[0] : event;
        this.offset = { x: point.clientX - start.x, y: point.clientY - start.y };
        this.apply();
      };
      const up = () => {
        dragging = false;
      };
      this.stage.addEventListener('mousedown', down);
      this.stage.addEventListener('touchstart', down, { passive: true });
      window.addEventListener('mousemove', move);
      window.addEventListener('touchmove', move, { passive: true });
      window.addEventListener('mouseup', up);
      window.addEventListener('touchend', up);
    }

    setScale(value) {
      this.scale = Math.min(MAX_SCALE, Math.max(1, value));
      if (this.scale === 1) this.offset = { x: 0, y: 0 };
      this.apply();
    }

    apply() {
      this.image.style.transform = `translate(${this.offset.x}px, ${this.offset.y}px) scale(${this.scale})`;
      this.stage.classList.toggle('-zoomed', this.scale > 1);
    }

    open(items, index) {
      this.build();
      this.items = items;
      this.index = index;
      this.root.classList.add('-open');
      this.root.querySelector('.mb-zoom-prev').hidden = items.length < 2;
      this.root.querySelector('.mb-zoom-next').hidden = items.length < 2;
      document.documentElement.classList.add('-no-scroll');
      this.render();
      this.releaseFocus = window.MB.trapFocus(this.root, this.root.querySelector('.mb-zoom-close'));
    }

    render() {
      const item = this.items[this.index];
      if (!item) return;
      this.setScale(1);
      this.image.src = item.src;
      this.image.alt = item.alt || '';
    }

    go(step) {
      if (this.items.length < 2) return;
      this.index = (this.index + step + this.items.length) % this.items.length;
      this.render();
    }

    close() {
      if (!this.root) return;
      this.root.classList.remove('-open');
      document.documentElement.classList.remove('-no-scroll');
      if (this.releaseFocus) this.releaseFocus();
      if (this.trigger) this.trigger.focus();
    }
  }

  const lightbox = new Lightbox();

  /** Abre o lightbox com a lista de imagens informada, a partir do índice dado. */
  function openZoom(items, index, trigger) {
    lightbox.trigger = trigger || null;
    lightbox.open(items, index);
  }

  window.MB = Object.assign(window.MB || {}, { openZoom });
})();
